import { Controller } from "@hotwired/stimulus"

// Connects to data-controller="field-error-focus"
//
// On a failed event submit, the server sends the list of invalid fields
// (selector + message). This controller, rendered into the page on failure,
// marks every invalid field (aria-invalid, red outline, inline message) and
// scrolls/focuses the FIRST one in DOM order so the user is taken straight to
// the field they need to fix. Highlights clear as soon as the user edits a
// field.
export default class extends Controller {
  static values = { errors: Array }

  connect() {
    const errors = this.errorsValue || []
    if (errors.length === 0) return

    // Resolve to live elements, drop any that aren't on the page, and order by
    // real DOM position (server order may not match form layout).
    const targets = errors
      .map((e) => ({ el: document.querySelector(e.selector), message: e.message }))
      .filter((t) => t.el)
      .sort((a, b) =>
        a.el.compareDocumentPosition(b.el) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1
      )

    if (targets.length === 0) return

    targets.forEach((t) => this.markInvalid(t.el, t.message))

    // Scroll/focus the first field the user can actually see. Some inputs are
    // visually hidden behind a custom widget (e.g. the timezone select is 1px;
    // the real control is its wrapper), so a hidden element is a poor scroll
    // target and can't take focus.
    const firstVisible =
      targets.find((t) => this.isVisible(t.el)) || targets[0]
    const scrollEl = this.scrollAnchorFor(firstVisible.el)

    requestAnimationFrame(() => {
      const top = scrollEl.getBoundingClientRect().top + window.scrollY - 96
      window.scrollTo({ top, behavior: "smooth" })
      try {
        firstVisible.el.focus({ preventScroll: true })
      } catch (_) {
        /* element may not be focusable; the scroll + highlight still guide the user */
      }
    })
  }

  isVisible(el) {
    if (!el || el.offsetParent === null) return false
    const r = el.getBoundingClientRect()
    return r.width > 1 && r.height > 1
  }

  // The visible "control box" to highlight. Composite date/time/select fields
  // wrap the real input in a styled box (and pair it with an icon in a flex
  // row), so highlight that box — for date AND time, whether or not the inner
  // input is itself visible — to keep the outline tight and consistent. Plain
  // inputs/selects use the element itself.
  controlBoxFor(el) {
    // Prefer the composite control box when the field lives in one.
    const composite = el.closest(".date-field-wrapper, .select-wrapper")
    if (composite && this.isVisible(composite)) return composite

    if (this.isVisible(el)) return el

    // Fallbacks for any other hidden field.
    for (const sel of [".date-input-wrapper", ".field-wrapper"]) {
      const w = el.closest(sel)
      if (w && this.isVisible(w)) return w
    }
    return el
  }

  scrollAnchorFor(el) {
    return this.controlBoxFor(el)
  }

  // The element to insert the inline message after. If the control box sits in
  // a horizontal flex row (e.g. date input + calendar button, or two fields
  // side by side), inserting right after it would place the message beside the
  // field. Climb to the first ancestor whose parent is a block/column layout
  // so the message lands on its own line beneath the field.
  messageAnchorFor(box) {
    let node = box
    let parent = node.parentElement
    let hops = 0
    while (parent && hops < 4) {
      const cs = getComputedStyle(parent)
      const isRow = cs.display.includes("flex") && cs.flexDirection.startsWith("row")
      if (!isRow) break
      node = parent
      parent = node.parentElement
      hops++
    }
    return node
  }

  markInvalid(el, message) {
    el.setAttribute("aria-invalid", "true")
    const box = this.controlBoxFor(el)
    box.classList.add("field-error-highlight")
    el._fefHighlightEl = box

    // Inline message: insert after a block-level container so it lands on its
    // own line beneath the field, not squeezed into a flex row beside it. The
    // message leads with a circle-alert icon (design system field-error state).
    if (message) {
      const msgId = (el.id || el.name || "field") + "_inline_error"
      let msg = document.getElementById(msgId)
      if (!msg) {
        msg = document.createElement("div")
        msg.id = msgId
        msg.className = "field-error-inline"
        this.messageAnchorFor(box).insertAdjacentElement("afterend", msg)
      }
      // Lucide circle-alert (matches lucide_icon "circle-alert"), then the text.
      msg.innerHTML =
        '<svg class="field-msg-icon" width="14" height="14" viewBox="0 0 24 24" ' +
        'fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" ' +
        'stroke-linejoin="round" aria-hidden="true">' +
        '<circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/>' +
        '<line x1="12" y1="16" x2="12.01" y2="16"/></svg>' +
        '<span class="field-error-text"></span>'
      msg.querySelector(".field-error-text").textContent = message
      el.setAttribute("aria-describedby", msgId)
    }

    // Clear the error state the moment the user starts fixing the field.
    const clear = () => this.clearInvalid(el)
    el.addEventListener("input", clear, { once: true })
    el.addEventListener("change", clear, { once: true })
  }

  clearInvalid(el) {
    const highlightEl = el._fefHighlightEl || el
    highlightEl.classList.remove("field-error-highlight")
    el.removeAttribute("aria-invalid")
    const msgId = el.getAttribute("aria-describedby")
    if (msgId) {
      const msg = document.getElementById(msgId)
      if (msg && msg.classList.contains("field-error-inline")) msg.remove()
      el.removeAttribute("aria-describedby")
    }
  }
}
